// =====================================================================
// Zod schemas for every public table + RPC.
// Single source of truth for runtime validation AND TypeScript types
// (`z.infer<typeof Schema>`). NO null, NO undefined, NO any, NO
// hardcoded constants — everything is data.
// =====================================================================

import { z } from "zod";

// ---------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------
const ZERO_UUID = "00000000-0000-0000-0000-000000000000";
export const ZERO_UUID_SENTINEL = ZERO_UUID;

export const uuid = z.string().uuid();
export const timestamp = z.string().datetime({ offset: true }).or(z.string());
export const text = z.string().default("");
export const nonEmptyText = z.string().min(1);
export const int0 = z.number().int().nonnegative().default(0);
export const num0 = z.number().nonnegative().default(0);
export const positiveNum = z.number().positive();

/** All monetary columns in the DB use `numeric(14,2)`.
 *  See migration 0019_standardize_money.sql for the rationale.
 *  14 total digits, 2 after the decimal, max value 100 trillion.
 *  Currency-agnostic — the column does not know whether it holds
 *  RUB, USD, UZS or any other fiat. The currency is metadata on
 *  the row (e.g. `orders.currency`) or in `settings`. */
export const money = z.number().nonnegative();
export const price = z.number().positive();

/** Compile-time marker for the `numeric(14,2)` money column type.
 *  Used in JSDoc on schemas that map to money columns, so the
 *  DB-side precision is discoverable from the schema file alone. */
export const MONEY_COLUMN_TYPE = "numeric(14,2)" as const;

export const idArray = z.array(uuid).default([]);
export const bool = z.boolean().default(false);
export const json = z.unknown();

// ---------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------
export type ProductStatus = z.infer<typeof ProductStatusSchema>;
export const ProductStatusSchema = z.enum(["active", "hidden", "draft", "archived"]);

/** 5 states, of which the first 4 are user-pickable in the admin
 *  order-status editor; 'delivered' is system-only (set by
 *  pickup-verify / courier panel). */
export const OrderStatusSchema = z.enum([
  "new",
  "packed",
  "delivering",
  "partial",
  "delivered",
  "cancelled",
]);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;
export const ORDER_USER_PICKABLE_STATUSES = [
  "new",
  "packed",
  "delivering",
  "cancelled",
] as const;
export const ORDER_TERMINAL_STATUSES = ["delivered", "cancelled"] as const;

export const DeliveryMethodSchema = z.enum(["pickup", "courier"]);
export type DeliveryMethod = z.infer<typeof DeliveryMethodSchema>;
export const DictionaryKindSchema = z.enum([
  "gender", "color", "size", "brand", "material", "season", "country", "badge",
]);
export type BannerLinkType = z.infer<typeof BannerLinkTypeSchema>;
export const BannerLinkTypeSchema = z.enum(["none", "product", "category", "brand", "url", "search"]);

// ---------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------
export const UserSchema = z.object({
  id: uuid,
  tg_id: z.number().int().nonnegative(),
  first_name: text,
  last_name: text,
  username: text,
  photo_url: text,
  language_code: text,
  is_premium: bool,
  role_id: uuid.nullable().catch(null),
  locale: z.string().default("ru"),
  // Self-managed contact + location (see migration 0015).
  // TG account supplies first/last name + photo_url. The user can
  // override first/last name in profile/checkout, and shares phone
  // + geo via Telegram native APIs (`requestContact`,
  // `LocationManager.requestLocation`).
  phone: text.nullable().catch(null),
  geo_lat: z.number().min(-90).max(90).nullable().catch(null),
  geo_lng: z.number().min(-180).max(180).nullable().catch(null),
  // Official employee photo (see migration 0016). Separate from
  // `photo_url` (TG avatar) — uploaded by the user to S3 after
  // onboarding. Shown to the customer in the delivery notification
  // for identity verification (anti-impersonation).
  employee_photo_url: text.nullable().catch(null),
  // Client-uploaded avatar (see migration 0017). Overrides the TG
  // `photo_url` for display purposes (profile, nav, etc.). The user
  // taps the avatar in the profile page to upload a new image. Stored
  // in the `eurostyle-public` S3 bucket under `avatars/`.
  avatar_url: text.nullable().catch(null),
  banned_at: timestamp.nullable().catch(null),
  vip_at: timestamp.nullable().catch(null),
  last_seen_at: timestamp,
  created_at: timestamp,
  updated_at: timestamp,
  deleted_at: timestamp.nullable().catch(null),
});
export type User = z.infer<typeof UserSchema>;

// ---------------------------------------------------------------------
// RBAC — roles, permissions, role_permissions
// ---------------------------------------------------------------------
const i18nName = z.object({
  ru: z.string().default(""),
  en: z.string().default(""),
  uz: z.string().default(""),
});

export const RoleSchema = z.object({
  id: uuid,
  slug: nonEmptyText,
  name: i18nName,
  description: i18nName,
  is_builtin: bool,
  sort: int0,
  created_at: timestamp,
  updated_at: timestamp,
});
export type Role = z.infer<typeof RoleSchema>;

export const PermissionSchema = z.object({
  id: uuid,
  key: nonEmptyText,
  category: nonEmptyText,
  name: i18nName,
  description: i18nName,
  sort: int0,
  created_at: timestamp,
});
export type Permission = z.infer<typeof PermissionSchema>;

export const RolePermissionSchema = z.object({
  role_id: uuid,
  permission_id: uuid,
  granted_at: timestamp,
});
export type RolePermission = z.infer<typeof RolePermissionSchema>;

export const PRESET_ROLE_SLUGS = ["client", "delivery", "manager", "admin"] as const;
export type PresetRoleSlug = (typeof PRESET_ROLE_SLUGS)[number];

// ---------------------------------------------------------------------
// User profile (rich, returned by telegram-auth Edge Function)
// ---------------------------------------------------------------------
export const LocaleSchema = z.enum(["ru", "en", "uz"]);
export type Locale = z.infer<typeof LocaleSchema>;

/** Full profile, single source of truth for the Mini App + bot. */
export const UserProfileSchema = z.object({
  user_id: uuid,
  tg_id: z.number().int().nonnegative(),
  first_name: text,
  last_name: text,
  username: text,
  photo_url: text,
  language_code: text,
  is_premium: bool,
  role_slug: z.string().min(1),
  role_id: uuid,
  permissions: z.array(nonEmptyText),
  locale: LocaleSchema,
  is_banned: bool,
  is_vip: bool,
  is_admin: bool,
  // Self-managed contact + location (see migration 0015).
  phone: text.nullable().catch(null),
  geo_lat: z.number().min(-90).max(90).nullable().catch(null),
  geo_lng: z.number().min(-180).max(180).nullable().catch(null),
  // Official employee photo (see migration 0016).
  employee_photo_url: text.nullable().catch(null),
  // Client-uploaded avatar (see migration 0017). Overrides TG photo_url.
  avatar_url: text.nullable().catch(null),
  // Адрес доставки (migration 0039). Заполняется из чекаута / «Мои адреса».
  address: text.catch(""),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

// ---------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------
export const CategorySchema = z.object({
  id: uuid,
  parent_id: uuid.nullable().catch(null),
  slug: nonEmptyText,
  name: nonEmptyText,
  description: text,
  icon: text,
  image_url: text,
  sort_order: int0,
  active: z.boolean().default(true),
  created_at: timestamp,
  updated_at: timestamp,
  deleted_at: timestamp.nullable().catch(null),
});
export type Category = z.infer<typeof CategorySchema>;

export interface CategoryNode extends Category {
  children: CategoryNode[];
}

// ---------------------------------------------------------------------
// Dictionary
// ---------------------------------------------------------------------
export const DictionaryEntrySchema = z.object({
  id: uuid,
  kind: DictionaryKindSchema,
  slug: nonEmptyText,
  name: text,
  hex_code: text,
  description: text,
  meta: z.record(z.string(), z.unknown()).default({}),
  sort_order: int0,
  active: z.boolean().default(true),
  created_at: timestamp,
  updated_at: timestamp,
  deleted_at: timestamp.nullable().catch(null),
});
export type DictionaryEntry = z.infer<typeof DictionaryEntrySchema>;
export type DictionaryEntryInput = Omit<DictionaryEntry, "id" | "created_at" | "updated_at" | "deleted_at">;
export type DictionaryEntryUpdate = Partial<Omit<DictionaryEntryInput, "kind" | "slug">>;

export type DictionaryKind = z.infer<typeof DictionaryKindSchema>;
export const DICTIONARY_KINDS = DictionaryKindSchema.options;

// ---------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------
export const ProductSchema = z.object({
  id: uuid,
  article: nonEmptyText,
  name: nonEmptyText,
  description: text,
  category_id: uuid,
  gender_id: uuid,
  country_id: uuid,
  brand_ids: idArray,
  color_ids: idArray,
  size_ids: idArray,
  material_ids: idArray,
  season_ids: idArray,
  base_price: price,
  old_price: money,
  badge: text,
  status: ProductStatusSchema,
  composition: text,
  created_at: timestamp,
  updated_at: timestamp,
  deleted_at: timestamp.nullable().catch(null),
});
export type Product = z.infer<typeof ProductSchema>;

/** Row returned by rpc_catalog_filter (denormalized for the grid). */
export const ProductCardSchema = ProductSchema.extend({
  category_name: nonEmptyText,
  category_slug: nonEmptyText,
  gender_name: nonEmptyText,
  gender_slug: nonEmptyText,
  country_name: nonEmptyText,
  brand_name: text, // primary brand (brand_ids[0]); "" when none
  main_image_url: text,
  in_stock: z.boolean(),
  variant_count: int0,
  rating: z.coerce.number().min(0).max(5).default(0),
  reviews_count: int0.default(0),
});
export type ProductCard = z.infer<typeof ProductCardSchema>;

// ---------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------
export const ProductVariantSchema = z.object({
  id: uuid,
  product_id: uuid,
  color_id: uuid,
  size_id: uuid,
  sku: text,
  stock: int0,
  price_override: money,
  cost_price: money,
  created_at: timestamp,
  updated_at: timestamp,
});
export type ProductVariant = z.infer<typeof ProductVariantSchema>;

// ---------------------------------------------------------------------
// Images
// ---------------------------------------------------------------------
export const ProductImageSchema = z.object({
  id: uuid,
  product_id: uuid,
  color_id: uuid,
  url: nonEmptyText,
  alt: text,
  is_main: z.boolean().default(false),
  sort_order: int0,
  created_at: timestamp,
});
export type ProductImage = z.infer<typeof ProductImageSchema>;

// ---------------------------------------------------------------------
// Reviews
// ---------------------------------------------------------------------
export const ReviewStatusSchema = z.enum(["pending", "approved", "hidden", "rejected"]);
export const ReviewSchema = z.object({
  id: uuid,
  product_id: uuid,
  user_id: uuid,
  rating: z.number().int().min(1).max(5),
  text: text,
  photos: z.array(nonEmptyText).default([]),
  status: ReviewStatusSchema,
  response: text,
  responded_at: timestamp.nullable().catch(null),
  created_at: timestamp,
});
export type Review = z.infer<typeof ReviewSchema>;

// ---------------------------------------------------------------------
// Cart / favorites (CloudStorage shape, NOT DB tables)
// ---------------------------------------------------------------------
export const CartItemSchema = z.object({
  productId: uuid,
  productName: nonEmptyText,
  // `article` and `variantId` are needed at order-create time:
  //   * article → `order_items.product_article_snapshot` (auditable
  //     in the admin panel without joining `products`).
  //   * variantId → `order_items.variant_id` (decrement stock on
  //     `product_variants.stock` server-side).
  // Both are defaulted to "" / null so existing cart items in
  // localStorage / CloudStorage keep loading after the upgrade.
  article: z.string().default(""),
  variantId: z.string().uuid().nullable().default(null),
  brand: text,
  price: price,
  oldPrice: money,
  image: text,
  color: text,
  size: text,
  qty: z.number().int().positive(),
});
export type CartItem = z.infer<typeof CartItemSchema>;
export type CartDraft = Omit<CartItem, "qty">;

export const FavoriteRefSchema = z.object({
  productId: uuid,
  color: text,
  addedAt: z.number().int().positive(),
});
export type FavoriteRef = z.infer<typeof FavoriteRefSchema>;

// ---------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------
export const OrderSchema = z.object({
  id: uuid,
  user_id: uuid,
  status: OrderStatusSchema,
  delivery_method: DeliveryMethodSchema,
  pickup_code: z
    .string()
    .regex(/^[0-9A-HJKMNP-TV-Z]{8}$/, "invalid pickup code")
    .nullable()
    .catch(null),
  pickup_verified_at: timestamp.nullable().catch(null),
  pickup_verified_by: uuid.nullable().catch(null),
  subtotal: money,
  discount: money,
  shipping_cost: money,
  total_amount: money,
  contact_name: nonEmptyText,
  contact_phone: nonEmptyText,
  contact_email: text,
  delivery_address: text,
  delivery_city: text,
  delivery_postal_code: text,
  delivery_country: text,
  scheduled_delivery_date: z.string().date(),
  recipient_pays: bool,
  assigned_courier_id: uuid.nullable().catch(null),
  pickup_point_id: uuid.nullable().catch(null),
  delivery_zone_id: uuid.nullable().catch(null),
  promo_code_id: uuid.nullable().catch(null),
  payment_method: text,
  notes: text,
  paid_at: timestamp.nullable().catch(null),
  packed_at: timestamp.nullable().catch(null),
  shipped_at: timestamp.nullable().catch(null),
  delivered_at: timestamp.nullable().catch(null),
  cancelled_at: timestamp.nullable().catch(null),
  created_at: timestamp,
  updated_at: timestamp,
});
export type Order = z.infer<typeof OrderSchema>;

export const OrderItemSchema = z.object({
  id: uuid,
  order_id: uuid,
  product_id: uuid.nullable().catch(null),
  variant_id: uuid.nullable().catch(null),
  product_name_snapshot: nonEmptyText,
  product_article_snapshot: nonEmptyText,
  color_name_snapshot: text,
  size_name_snapshot: text,
  unit_price: price,
  cost_price_snapshot: money,
  quantity: z.number().int().positive(),
  total_price: money,
  created_at: timestamp,
});
export type OrderItem = z.infer<typeof OrderItemSchema>;

// ---------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------
export const BannerSchema = z.object({
  id: uuid,
  title: text,
  subtitle: text,
  image_url: text,
  link_type: BannerLinkTypeSchema,
  link_value: text,
  position: int0,
  active: z.boolean().default(true),
  starts_at: timestamp,
  ends_at: timestamp.nullable().catch(null),
  created_at: timestamp,
  updated_at: timestamp,
});
export type Banner = z.infer<typeof BannerSchema>;

// ---------------------------------------------------------------------
// Settings (typed accessor for known keys)
// ---------------------------------------------------------------------
export const KNOWN_SETTING_KEYS = [
  "currency",
  "currency_symbol",
  "app_name",
  "support_handle",
  "support_url",
  "tg_channel_url",
  "bot_username",
  "min_order_amount",
  "free_shipping_from",
  "delivery_cost",
  "contact_phone",
  "contact_email",
  "legal_inn",
  "legal_name",
  "legal_address",
  "legal_address_geo_lat",
  "legal_address_geo_lng",
  "default_locale",
  "promo_badge",
  "hit_badge",
  "new_badge",
  "policy_return",
  "policy_payment",
  "policy_privacy",
  "banners_fallback_title",
  "banners_fallback_subtitle",
] as const;
export type KnownSettingKey = (typeof KNOWN_SETTING_KEYS)[number];

export const SettingSchema = z.object({
  key: nonEmptyText,
  value: json,
  description: text,
  updated_at: timestamp,
});
export type Setting = z.infer<typeof SettingSchema>;

/** All settings as a typed object. */
export type AppSettings = Record<KnownSettingKey, unknown>;

// ---------------------------------------------------------------------
// Visits (client-side analytics payload)
// ---------------------------------------------------------------------
export const VisitSchema = z.object({
  tg_id: int0,
  path: text,
  user_agent: text,
  referer: text,
});
export type Visit = z.infer<typeof VisitSchema>;
export type VisitInput = Partial<Visit>;

// ---------------------------------------------------------------------
// Catalog filters
// ---------------------------------------------------------------------
export const CatalogFiltersSchema = z.object({
  categories: z.array(uuid).nullable().default(null),
  genders: z.array(uuid).nullable().default(null),
  brands: z.array(uuid).nullable().default(null),
  colors: z.array(uuid).nullable().default(null),
  sizes: z.array(uuid).nullable().default(null),
  materials: z.array(uuid).nullable().default(null),
  seasons: z.array(uuid).nullable().default(null),
  minPrice: z.number().nullable().default(null),
  maxPrice: z.number().nullable().default(null),
  search: z.string().nullable().default(null),
  badge: z.string().nullable().default(null),
  sort: z.enum(["newest", "price_asc", "price_desc"]).default("newest"),
  limit: int0.default(24),
  offset: int0.default(0),
});
export type CatalogFilters = z.infer<typeof CatalogFiltersSchema>;

// ---------------------------------------------------------------------
// Auth response from telegram-auth Edge Function
// ---------------------------------------------------------------------
/** Back-compat alias — the Edge Function now returns a full
 *  UserProfile but the public type stays compatible. */
export type AuthUser = UserProfile;
